import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  ListItem,
  useAHTextTypeStyles,
  useLayoutContext,
  useTheme,
} from "arkh";

import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  graphql,
  useLazyLoadQuery,
  useMutation,
  useSubscription,
} from "react-relay";

import { useWorkspaceSignalEditAddImage } from "./useWorkspaceSignalEditAddImage";
import { WorkspaceSignalEditMainQuery } from "./__generated__/WorkspaceSignalEditMainQuery.graphql";
import {
  WorkspaceSignalEditRouteProp,
  WorkspaceSignalEditNavigationProp,
} from "../WorkspaceSignalEditStack";
import { OrgButton } from "../../../OrgButton";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import {
  SignalMainContent,
  SignalMainContentRef,
} from "./WorkspaceSignalEditMainContent";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  DropFileZone,
  droppedFileToImage,
} from "../../../../Files/DropFileZone";
import { WorkspaceSignalEditMainSubscription } from "./__generated__/WorkspaceSignalEditMainSubscription.graphql";

import { FormattedMessage } from "react-intl";

import { ScrollView } from "react-native-gesture-handler";

import { WorkspaceSignalEditMainPushNotification } from "./WorkspaceSignalEditMainPushNotification";

import { WebModalHeader } from "../../../WorkspaceTodos/WorkspaceTodoEdit/WorkspaceTodoEditMain/WebModalHeader";
import { EditedBy } from "./WorkspaceSignalEditMainEditedInfo";
import { DeleteDraft } from "./WorkspaceSignalEditMainDelete";
import { SignalPrioritySelector } from "./WorkspaceSignalEditMainPrioritySelector";
import { WorkspaceTodoDetailIndicator } from "../../../WorkspaceTodos/DraftIndicator";
import { ReciversListItemButton } from "../../../WorkspaceTodos/WorkspaceTodoEdit/WorkspaceTodoEditMain/WorkspaceRecieversListItemButton";
import { FormattedDateInWorkspace } from "../../../../../common/dates/FormattedDate";
import { WorkspaceSignalEditMainCancelPublishMutation } from "./__generated__/WorkspaceSignalEditMainCancelPublishMutation.graphql";
import { SignalWorkspaceTemplateSelector } from "../../../WorkspaceTodoSignalShared/Templates/SignalWorkspaceTemplateSelector";
import { WorkspaceSignalEditMainProducts } from "./WorkspaceSignalEditMainProducts";

export const WorkspaceSignalEditMain = (props: {
  route: WorkspaceSignalEditRouteProp<"WorkspaceSignalEditMain">;
  navigation: WorkspaceSignalEditNavigationProp<"WorkspaceSignalEditMain">;
}) => (
  <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
    {(fk) => (
      <InnerWorkspaceSignalEditMain
        navigation={props.navigation}
        route={props.route}
        fetchKey={fk}
      />
    )}
  </LazyQueryNetworkBoundrary>
);

function InnerWorkspaceSignalEditMain(props: {
  route: WorkspaceSignalEditRouteProp<"WorkspaceSignalEditMain">;
  navigation: WorkspaceSignalEditNavigationProp<"WorkspaceSignalEditMain">;
  fetchKey: string;
}) {
  const data = useLazyLoadQuery<WorkspaceSignalEditMainQuery>(
    graphql`
      query WorkspaceSignalEditMainQuery(
        $id: ID!
        $workspaceId: ID!
        $createIfNotExist: Boolean!
      ) {
        organisation {
          features
        }
        workspace(id: $workspaceId) @required(action: THROW) {
          id

          ...SignalWorkspaceTemplateSelector_workspace
          signal(id: $id, createIfNotExist: $createIfNotExist)
            @required(action: THROW) {
            id
            receiverStores {
              id
            }
            potentialReceiverStores {
              __typename
            }
            scheduledPublishAt {
              ...FormattedDate
            }
            status
            ...SignalWorkspaceTemplateSelector_SignalWorkspace
            ...WorkspaceSignalEditMainPrioritySelector_Signal
            ...WorkspaceSignalEditMainEditedInfo_Signal
            ...WorkspaceSignalEditMainContent_Signal
            ...WorkspaceSignalEditMainProducts_Signal
            ...WorkspaceSignalEditMainPushNotification_signalWorkspace
            ...WorkspaceRecieversListItemButton_workspaceEntity
          }
        }
      }
    `,
    {
      id: props.route.params.id,
      workspaceId: props.route.params.workspaceId,
      createIfNotExist: props.route.params.create === true,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const disabled = data.workspace.signal.status !== "DRAFT";

  useSubscription<WorkspaceSignalEditMainSubscription>(
    useMemo(
      () => ({
        variables: {
          signalWorkspaceId: data.workspace.signal.id,
          workspaceId: data.workspace.id,
        },
        subscription: graphql`
          subscription WorkspaceSignalEditMainSubscription(
            $workspaceId: ID!
            $signalWorkspaceId: ID!
          ) {
            signalWorkspaceChanged(
              signalWorkspaceIds: [$signalWorkspaceId]
              workspaceId: $workspaceId
            ) {
              id
              status
              signalCount
              ...WorkspaceSignalEditMainPrioritySelector_Signal
              ...WorkspaceSignalEditMainEditedInfo_Signal
              ...WorkspaceSignalEditMainContent_Signal
              ...WorkspaceSignalEditMainProducts_Signal
              ...WorkspaceSignalEditMainPushNotification_signalWorkspace

              ...WorkspaceRecieversListItemButton_workspaceEntity
            }
          }
        `,
      }),
      [data.workspace.id, data.workspace.signal.id]
    )
  );

  const signal = data.workspace.signal;

  const addImage = useWorkspaceSignalEditAddImage(signal.id);

  const [statuses, setStatuses] = useState<{
    [key: string]: "saving" | "saved" | "error";
  }>({});

  const setStatus = useCallback(
    (key: string, status: "saving" | "saved" | "error") => {
      setStatuses((statuses) => ({ ...statuses, [key]: status }));
    },
    []
  );

  const status = useMemo(() => {
    if (Object.values(statuses).some((val) => val === "error")) {
      return "error";
    }
    if (Object.values(statuses).some((val) => val === "saving")) {
      return "saving";
    }
    return "saved";
  }, [statuses]);

  const text = useAHTextTypeStyles("subtitle");
  const theme = useTheme();
  useLayoutEffect(() => {
    props.navigation.setOptions({
      header: () => (
        <WebModalHeader
          title={
            disabled ? null : (
              <>
                <WebModalHeader.Title>
                  <FormattedMessage
                    defaultMessage="Edit Signal"
                    description="Workspace edit signal screen title"
                  />
                </WebModalHeader.Title>
                {signal.status === "DRAFT" ? (
                  <SignalWorkspaceTemplateSelector
                    workspace={data.workspace}
                    signalWorkspace={data.workspace.signal}
                  />
                ) : null}
              </>
            )
          }
          close={
            <>
              <EditedBy signal={data.workspace.signal} />
              <WorkspaceTodoDetailIndicator
                status={data.workspace.signal.status}
              />
              <WebModalHeader.CloseButton onPress={props.navigation.goBack} />
            </>
          }
        />
      ),
    });
  }, [
    data.workspace.signal,
    props.navigation,
    props.route.params.workspaceId,
    status,
    text,
    theme.surface,
    signal.id,
    disabled,
    signal.status,
    data.workspace,
  ]);

  const fileRef = useRef<SignalMainContentRef>(null);

  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();

  const [cancelPublish, cancelPublishInFlight] =
    useMutation<WorkspaceSignalEditMainCancelPublishMutation>(graphql`
      mutation WorkspaceSignalEditMainCancelPublishMutation($id: ID!)
      @raw_response_type {
        signalWorkspace(id: $id) {
          updateScheduledPublishAt(newPublishAt: null) {
            id
            status
            scheduledPublishAt {
              isoString
              ...FormattedDate
            }
          }
        }
      }
    `);

  return (
    <DropFileZone
      type="FILE_AND_IMAGE"
      deactivated={disabled}
      onDropFilesNew={async (files) => {
        files
          .filter((v) => v.abstractType === "IMAGE")
          .forEach(async ({ file, abstractType }) => {
            if (abstractType === "IMAGE") {
              const { width, height, uri } = await droppedFileToImage(file);
              addImage(uri, { width, height });
            }
          });

        const file_files = files.filter(
          ({ abstractType }) => abstractType === "FILE"
        );
        if (file_files.length > 0) {
          fileRef.current?.showFilesModal(file_files);
        }
      }}
      maxSize={10000000}
    >
      <Box style={{ flexDirection: "column", flex: 1 }}>
        <Box
          //space="small"
          spaceBottom="none"
          color="background"
          style={{ flex: 1, flexDirection: "row" }}
        >
          <Box
            style={{ flex: 1, maxWidth: "60%" }}
            spaceLeft="medium"
            spaceVertical="medium"
            spaceRight="small"
          >
            <SignalMainContent
              ref={fileRef}
              signal={signal}
              disabled={disabled}
              workspaceId={props.route.params.workspaceId}
              setStatus={setStatus}
              addImage={addImage}
            />
          </Box>
          <ScrollView
            style={{ flex: 1, maxWidth: "40%" }}
            contentContainerStyle={{
              padding: layout.space.medium,
              paddingLeft: layout.space.small,
            }}
          >
            {data.organisation?.features.includes("PRODUCTS") ? (
              <Box spaceBottom="medium">
                <WorkspaceSignalEditMainProducts
                  signal={signal}
                  disabled={disabled}
                  openLinkProduct={({
                    linkedProductIds,
                    signalWorkspaceId,
                  }) => {
                    props.navigation.navigateDeprecated(
                      "WorkspaceSignalEditLinkProduct",
                      {
                        linkedProductIds,
                        signalWorkspaceId,
                        workspaceId: props.route.params.workspaceId,
                      }
                    );
                  }}
                  openProduct={(id) => {
                    props.navigation.navigateDeprecated("WorkspaceProduct", {
                      id,
                    });
                  }}
                />
              </Box>
            ) : null}
            <ListItem
              isFirst
              disabled
              border="border-primary"
              color={disabled ? "transparent" : "surface-primary"}
              left={<AHIcon type="prio" color="darkText" />}
              right={
                <SignalPrioritySelector signal={signal} disabled={disabled} />
              }
            >
              <ListItem.TitleSecondary>
                {intl_tokens.Priority}
              </ListItem.TitleSecondary>
            </ListItem>
            <AHSeparator />
            <WorkspaceSignalEditMainPushNotification
              signalWorkspace={data.workspace.signal}
              disabled={disabled}
            />
            <Box spaceTop="medium">
              <ReciversListItemButton
                onPress={() => {
                  props.navigation.navigateDeprecated(
                    "WorkspaceSignalReceivers",
                    {
                      workspaceId: props.route.params.workspaceId,
                      signalId: signal.id,
                    }
                  );
                }}
                entity={data.workspace.signal}
              />
            </Box>
          </ScrollView>
        </Box>

        <Box
          style={{
            height: 60,
            width: "100%",
            justifyContent: "center",
          }}
          color="surface"
          spaceHorizontal="medium"
        >
          <Box
            style={{
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            {signal.status === "DRAFT" ? (
              <Box spaceRight="medium">
                <DeleteDraft
                  id={signal.id}
                  onDeleted={() => {
                    props.navigation.goBack();
                  }}
                />
              </Box>
            ) : null}

            <Box style={{ flex: 1 }} />

            {signal.status === "DRAFT" ? (
              <>
                <Box spaceRight="small">
                  <OrgButton
                    backgroundColor="transparent"
                    tintColor="primary"
                    onPress={() => {
                      props.navigation.pop();
                    }}
                    title={
                      <FormattedMessage
                        defaultMessage="Continue later"
                        description="Workspace Signal Continue Later button"
                      />
                    }
                  />
                </Box>
                <OrgButton
                  backgroundColor="primary"
                  tintColor="text-inverted"
                  onPress={() => {
                    props.navigation.navigateDeprecated(
                      "WorkspaceSignalSchedule",
                      {
                        workspaceId: props.route.params.workspaceId,
                        workspaceSignalId: signal.id,
                      }
                    );
                  }}
                  title={
                    <FormattedMessage
                      defaultMessage="Schedule"
                      description="Workspace Signal Schedule button"
                    />
                  }
                />
              </>
            ) : (
              <>
                <Box spaceRight="small">
                  {signal.scheduledPublishAt ? (
                    <AHText spaceRight="small" type="subtitle">
                      <FormattedMessage
                        defaultMessage="Scheduled for publication: {formattedDate}"
                        values={{
                          formattedDate: (
                            <FormattedDateInWorkspace
                              date={signal.scheduledPublishAt}
                            />
                          ),
                        }}
                        description="Workspace Signal Schedule scheduled for publication"
                      />
                    </AHText>
                  ) : null}
                </Box>

                <OrgButton
                  backgroundColor="background"
                  tintColor="text-primary"
                  loading={cancelPublishInFlight}
                  onPress={() => {
                    cancelPublish({
                      optimisticResponse: {
                        signalWorkspace: {
                          updateScheduledPublishAt: {
                            id: signal.id,
                            status: "DRAFT",
                            scheduledPublishAt: null,
                          },
                        },
                      },
                      variables: {
                        id: signal.id,
                      },
                    });
                  }}
                  title={
                    <FormattedMessage
                      defaultMessage="Cancel publication"
                      description="Workspace Signal Schedule cancel publish button"
                    />
                  }
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </DropFileZone>
  );
}
